import type {
  ICalculationRequest,
  ICalculationResult,
  ICloneTemplateOverride,
  IGradeLadderResult,
  IRatingMatrixFrameworkTemplate,
} from '__generated__';
import { baseApi } from 'store/api';
import {
  API,
  APIRead,
  asPatchBody,
  normalizeError,
  type PatchBody,
} from 'utils/api-client';
import type { IFullTemplate, IValidateWeightsResult } from './models';

export const ratingMatrixFrameworkTemplateApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getFullTemplate: builder.query<IFullTemplate, { id: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await APIRead.api.ratingMatrixFrameworkTemplateGetFullTemplateDetail({
            id,
          });
          return { data: data as IFullTemplate };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFrameworkTemplate', id: arg.id },
      ],
    }),

    createTemplate: builder.mutation<
      IRatingMatrixFrameworkTemplate,
      { frameworkId: number; payload: Partial<IRatingMatrixFrameworkTemplate> }
    >({
      async queryFn({ payload }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkTemplateCreateCreate(payload);
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFramework', id: arg.frameworkId },
      ],
    }),

    cloneTemplate: builder.mutation<
      IRatingMatrixFrameworkTemplate,
      { frameworkId: number; id: number; override: Partial<ICloneTemplateOverride> }
    >({
      async queryFn({ id, override }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkTemplateCloneCreate({ id }, override);
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFrameworkTemplate', id: arg.id },
        { type: 'RatingMatrixFramework', id: arg.frameworkId },
      ],
    }),

    archiveTemplate: builder.mutation<string, { frameworkId: number; id: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkTemplateArchiveCreate({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFrameworkTemplate', id: arg.id },
        { type: 'RatingMatrixFramework', id: arg.frameworkId },
      ],
    }),

    unarchiveTemplate: builder.mutation<string, { frameworkId: number; id: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkTemplateUnarchiveCreate({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFrameworkTemplate', id: arg.id },
        { type: 'RatingMatrixFramework', id: arg.frameworkId },
      ],
    }),

    patchTemplate: builder.mutation<
      string,
      { id: number; frameworkId: number; patch: PatchBody<IRatingMatrixFrameworkTemplate> }
    >({
      async queryFn({ id, patch }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkTemplatePatchPartialUpdate(
            { id },
            asPatchBody(patch),
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFrameworkTemplate', id: arg.id },
        { type: 'RatingMatrixFramework', id: arg.frameworkId },
      ],
    }),

    activateTemplate: builder.mutation<string, { id: number; frameworkId: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkTemplateActivateCreate({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFrameworkTemplate', id: arg.id },
        { type: 'RatingMatrixFramework', id: arg.frameworkId },
      ],
    }),

    lockTemplate: builder.mutation<string, { id: number; frameworkId: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkTemplateLockCreate({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFrameworkTemplate', id: arg.id },
        { type: 'RatingMatrixFramework', id: arg.frameworkId },
      ],
    }),

    validateTemplateWeights: builder.query<IValidateWeightsResult, { id: number }>({
      async queryFn({ id }) {
        try {
          const { data } =
            await APIRead.api.ratingMatrixFrameworkTemplateValidateWeightsDetail({ id });
          const result = data as { isValid?: boolean; errors?: string[] };
          return {
            data: {
              isValid: Boolean(result?.isValid),
              errors: Array.isArray(result?.errors) ? result.errors : [],
            },
          };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    calculateTemplate: builder.mutation<
      ICalculationResult,
      { id: number; request: Partial<ICalculationRequest> }
    >({
      async queryFn({ id, request }) {
        try {
          const { data } = await APIRead.api.ratingMatrixFrameworkTemplateCalculateCreate(
            { id },
            request,
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    getGradeLadder: builder.query<IGradeLadderResult, { id: number }>({
      async queryFn({ id }) {
        try {
          const { data } = await APIRead.api.ratingMatrixFrameworkTemplateGradeLadderDetail({
            id,
          });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: 'RatingMatrixFrameworkTemplate', id: `grade-ladder-${arg.id}` },
      ],
    }),
  }),
});

export const {
  useGetFullTemplateQuery,
  useCreateTemplateMutation,
  useCloneTemplateMutation,
  useArchiveTemplateMutation,
  useUnarchiveTemplateMutation,
  usePatchTemplateMutation,
  useActivateTemplateMutation,
  useLockTemplateMutation,
  useValidateTemplateWeightsQuery,
  useLazyValidateTemplateWeightsQuery,
  useCalculateTemplateMutation,
  useGetGradeLadderQuery,
} = ratingMatrixFrameworkTemplateApi;

